<template>
  <Teleport to="body">
    <transition name="stm-fade">
      <div v-if="show" class="stm-overlay" @click.self="$emit('close')">
        <div class="stm-card" role="dialog" aria-modal="true" aria-label="Survey template style">
          <h3 class="stm-title">Survey template style</h3>
          <p class="stm-subtitle">Choose which template renders this survey.</p>

          <div class="stm-options">
            <button
              v-for="opt in options"
              :key="opt.id"
              type="button"
              class="stm-option"
              :class="{ 'stm-option--active': opt.id === selected }"
              :aria-pressed="opt.id === selected"
              @click="$emit('select', opt.id)"
            >
              <span class="stm-option__check" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20 6 L9 17 L4 12" />
                </svg>
              </span>
              <span class="stm-option__body">
                <span class="stm-option__name">
                  {{ opt.name }}
                  <span v-if="opt.badge" class="stm-option__badge">{{ opt.badge }}</span>
                </span>
                <span class="stm-option__desc">{{ opt.desc }}</span>
              </span>
            </button>
          </div>

          <div class="stm-actions">
            <button type="button" class="stm-btn stm-btn--primary" @click="$emit('close')">Done</button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'

type Variant = 'classic' | 'africa'

const props = defineProps<{
  show: boolean
  selected: Variant
}>()

const emit = defineEmits<{
  (e: 'select', variant: Variant): void
  (e: 'close'): void
}>()

const options: { id: Variant; name: string; desc: string; badge?: string }[] = [
  // NOTE: internal ids stay 'classic'/'africa' (the stored quizConfig values);
  // only the display labels changed (Default / Colour Palette).
  {
    id: 'classic',
    name: 'Default',
    desc: 'The standard survey template. Unchanged.',
  },
  {
    id: 'africa',
    name: 'Colour Palette',
    badge: 'Testing',
    desc: 'New tile-based survey that uses your campaign colour palette (Look & Feel → Your Colours).',
  },
]

const onKey = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && props.show) {
    e.preventDefault()
    emit('close')
  }
}
onMounted(() => window.addEventListener('keydown', onKey))
onUnmounted(() => window.removeEventListener('keydown', onKey))
</script>

<style scoped>
.stm-overlay {
  position: fixed;
  inset: 0;
  z-index: 300;
  background: rgba(10, 13, 25, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  font-family: var(--font-sans);
}

.stm-card {
  width: 100%;
  max-width: 460px;
  background: #1a2332;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 26px 24px 20px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.45);
  box-sizing: border-box;
}

.stm-title {
  margin: 0 0 6px;
  font-size: 17px;
  font-weight: 700;
  color: #ffffff;
  text-align: center;
}
.stm-subtitle {
  margin: 0 0 18px;
  font-size: 13px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
  text-align: center;
}

.stm-options {
  display: grid;
  gap: 10px;
}

.stm-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  margin: 0;
  padding: 14px 14px;
  border-radius: 14px;
  border: 1.5px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.03);
  color: #ffffff;
  cursor: pointer;
  text-align: left;
  height: auto;
  min-height: unset;
  box-shadow: none;
  transition: border-color 150ms ease, background 150ms ease;
}
.stm-option:hover {
  border-color: rgba(150, 255, 201, 0.5);
  background: rgba(150, 255, 201, 0.06);
}
.stm-option--active {
  border-color: var(--vt-c-accent, #39ff14);
  background: rgba(57, 255, 20, 0.08);
}

.stm-option__check {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  margin-top: 1px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid rgba(255, 255, 255, 0.25);
  color: transparent;
  transition: background 150ms ease, border-color 150ms ease, color 150ms ease;
}
.stm-option--active .stm-option__check {
  background: var(--vt-c-accent, #39ff14);
  border-color: var(--vt-c-accent, #39ff14);
  color: #0a1628;
}

.stm-option__body {
  display: grid;
  gap: 4px;
}
.stm-option__name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
}
.stm-option__badge {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #0a1628;
  background: var(--vt-c-accent, #39ff14);
  border-radius: 999px;
  padding: 2px 8px;
}
.stm-option__desc {
  font-size: 12.5px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
}

.stm-actions {
  display: flex;
  justify-content: center;
  margin-top: 18px;
}
.stm-btn {
  padding: 9px 24px;
  border-radius: 10px;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.12s ease;
  min-width: 110px;
}
.stm-btn--primary {
  background: #96ffc9;
  color: #181b2f;
}
.stm-btn--primary:hover { background: #82e8b0; }

.stm-fade-enter-active,
.stm-fade-leave-active {
  transition: opacity 0.15s ease;
}
.stm-fade-enter-active .stm-card,
.stm-fade-leave-active .stm-card {
  transition: transform 0.15s ease;
}
.stm-fade-enter-from,
.stm-fade-leave-to {
  opacity: 0;
}
.stm-fade-enter-from .stm-card,
.stm-fade-leave-to .stm-card {
  transform: translateY(8px) scale(0.98);
}
</style>
